import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/providers/AuthProvider'
import { userPreferencesApi } from '@/api/userPreferencesApi'
import {
  EMPTY_PREFS,
  PREFERENCE_KEY_PREFIX,
  clearAllTablePrefs,
  clearTablePrefs,
  coerceTablePrefs,
  hasSavedPrefs,
  preferenceKeyFor,
  readTablePrefs,
  tablePrefsKey,
  writeTablePrefs,
  type TableColumnPrefs,
} from '@/lib/tableColumnPrefs'

/**
 * App-wide store for per-user table column preferences.
 *
 * ## Where the data lives
 *
 * The BACKEND owns it: one `UserPreference` row per user per table, under
 * `table.columns.<tableId>`. A column layout is a preference that belongs to a
 * person, so it follows them between machines and browsers. localStorage is a
 * cache in front of that (see `lib/tableColumnPrefs`), read only until the
 * server map arrives so a cold load does not flash the default columns.
 *
 * ## One provider for every table in the app
 *
 * Rather than each table talking to the API itself, for four reasons:
 *
 *  1. **One request, not forty-six.** The whole preference map is fetched once
 *     with `GET /preferences`. A per-table fetch would mean a request per table
 *     on every page.
 *  2. **The owner lives in one place.** Prefs are the signed-in user's; a table
 *     that resolved that itself would have to know about auth and would read a
 *     stale id for the render after a user switch.
 *  3. **Two views of one table stay in step.** The same `tableId` can be mounted
 *     twice (a page and a dialog, a list header and its row component). Holding
 *     the prefs here means hiding a column in one updates the other.
 *  4. **It makes "reset every layout" possible at all** — a per-table hook has
 *     no way to enumerate the others.
 *
 * ## Writes
 *
 * Optimistic and debounced. A change lands in local state immediately (the drag
 * has to look live), is written to the cache, and is PUT after a short quiet
 * period — per key, so toggling four columns in a row is four state updates but
 * one request each only once they settle. A pending write is flushed on
 * `pagehide`/`visibilitychange`, so closing the tab mid-debounce does not lose
 * the layout.
 *
 * A failed write is deliberately NOT rolled back. The user's intent was to hide
 * a column; snapping it back because a POST failed would be a worse outcome than
 * a layout that is correct for this session and re-saves on the next change.
 * Column layout is not data integrity.
 */

interface TableColumnPrefsContextValue {
  /** The current user's cache scope. 'anon' before sign-in resolves. */
  scope: string
  prefsFor: (tableId: string) => TableColumnPrefs
  setPrefsFor: (tableId: string, next: TableColumnPrefs) => void
  resetTable: (tableId: string) => void
  resetAll: () => void
  /** Table ids this user has customized — powers the "reset all" affordance. */
  customizedTableIds: string[]
}

const TableColumnPrefsContext = createContext<TableColumnPrefsContextValue | null>(null)

/** How long a change sits before it is saved. */
const WRITE_DEBOUNCE_MS = 600

export const TABLE_PREFS_QUERY_KEY = ['user-preferences'] as const

export function TableColumnPrefsProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useAuth()
  // `userId` is null for a principal with no DB row yet; `sub` is always present
  // on a resolved session, so it is the stabler cache scope of the two. (The
  // SERVER keys on the real user id and stores nothing for a rowless principal —
  // such a user keeps their layout for the session via the cache alone.)
  const scope = user?.userId ?? user?.sub ?? 'anon'
  const queryClient = useQueryClient()

  // The server map. `staleTime: Infinity` because this provider is the only
  // writer in the tab and it updates the cache itself on every save — a refetch
  // could only ever tell it what it already knows, or clobber a pending write.
  const { data: serverMap } = useQuery({
    queryKey: TABLE_PREFS_QUERY_KEY,
    queryFn: userPreferencesApi.getAll,
    enabled: isAuthenticated,
    staleTime: Infinity,
    retry: 1,
  })

  const [byTable, setByTable] = useState<Record<string, TableColumnPrefs>>({})

  /**
   * Cache-hydration map for tables read from localStorage but not yet written.
   *
   * A ref read during render rather than state set in an effect, for two reasons
   * that pull the same way. `readTablePrefs` builds a fresh object every call, so
   * returning it directly would hand a new identity to every consumer on every
   * render and defeat the `useMemo`s downstream. And hydrating in an effect would
   * render one frame at the defaults before the saved layout landed. Caching by
   * identity avoids both; the write is idempotent, so it is safe in render.
   */
  const cache = useRef<{ scope: string; map: Map<string, TableColumnPrefs> }>({
    scope,
    map: new Map(),
  })
  const [scopeOfState, setScopeOfState] = useState(scope)
  if (cache.current.scope !== scope) {
    cache.current = { scope, map: new Map() }
  }
  // Written prefs belong to the scope that wrote them. Clearing in render (not
  // an effect) means the first render after a user switch already shows the new
  // person's layouts rather than the previous one's.
  if (scopeOfState !== scope) {
    setScopeOfState(scope)
    setByTable({})
  }

  // Pending debounced writes, keyed by tableId.
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>())
  const pending = useRef(new Map<string, TableColumnPrefs | null>())

  /**
   * Send one pending write now.
   *
   * `unloading` is passed when the page is going away, and switches the request
   * to `keepalive` — a normal fetch is cancelled on unload, which would lose a
   * change still inside its debounce window.
   */
  const flush = useCallback((tableId: string, unloading = false) => {
    const timer = timers.current.get(tableId)
    if (timer) {
      clearTimeout(timer)
      timers.current.delete(tableId)
    }
    if (!pending.current.has(tableId)) return
    const next = pending.current.get(tableId) ?? null
    pending.current.delete(tableId)
    const key = preferenceKeyFor(tableId)
    const opts = unloading ? { keepalive: true } : undefined
    // Prefs back at their defaults are DELETED rather than stored as an empty
    // object, so a reset leaves no row behind and a table nobody customized
    // costs nothing.
    // A chosen sort alone is worth keeping too (z8nrz7e4h6) — hence
    // hasSavedPrefs, not the layout-only isCustomized.
    const save =
      next && hasSavedPrefs(next)
        ? userPreferencesApi.set(key, next, opts)
        : userPreferencesApi.remove(key, opts)
    void save
      .then(() => {
        queryClient.setQueryData<Record<string, unknown>>(TABLE_PREFS_QUERY_KEY, (prev) => {
          const map = { ...(prev ?? {}) }
          if (next && hasSavedPrefs(next)) map[key] = next
          else delete map[key]
          return map
        })
      })
      .catch(() => {
        // Left as-is on purpose — see the docblock. The layout is correct for
        // this session and the next change will try again.
      })
  }, [queryClient])

  const schedule = useCallback(
    (tableId: string, next: TableColumnPrefs | null) => {
      pending.current.set(tableId, next)
      const existing = timers.current.get(tableId)
      if (existing) clearTimeout(existing)
      timers.current.set(
        tableId,
        setTimeout(() => flush(tableId), WRITE_DEBOUNCE_MS),
      )
    },
    [flush],
  )

  // Closing the tab or backgrounding it mid-debounce must not lose the change.
  // `pagehide` rather than `beforeunload`, which is unreliable on mobile.
  useEffect(() => {
    const flushAll = (unloading: boolean) => {
      for (const tableId of [...timers.current.keys()]) flush(tableId, unloading)
    }
    const onPageHide = () => flushAll(true)
    const onHidden = () => {
      if (document.visibilityState === 'hidden') flushAll(true)
    }
    window.addEventListener('pagehide', onPageHide)
    document.addEventListener('visibilitychange', onHidden)
    return () => {
      window.removeEventListener('pagehide', onPageHide)
      document.removeEventListener('visibilitychange', onHidden)
      // Unmount is not an unload — the tab lives on, so an ordinary fetch is
      // fine and gets the response handling that keepalive requests skip.
      flushAll(false)
    }
  }, [flush])

  const prefsFor = useCallback(
    (tableId: string): TableColumnPrefs => {
      // This tab's own change wins: it is newer than anything fetched.
      const written = byTable[tableId]
      if (written) return written
      // Then the server, once it has arrived.
      if (serverMap) {
        const key = preferenceKeyFor(tableId)
        if (key in serverMap) {
          const cached = cache.current.map.get(tableId)
          const coerced = coerceTablePrefs(serverMap[key])
          // Reuse the cached object when it is value-equal, so a refetch does
          // not hand consumers a new identity for an unchanged layout.
          if (cached && JSON.stringify(cached) === JSON.stringify(coerced)) return cached
          cache.current.map.set(tableId, coerced)
          // Keep the local cache warm for the next cold load.
          writeTablePrefs(scope, tableId, coerced)
          return coerced
        }
        // Present in the map's absence: the server says this table has no saved
        // layout, so the defaults are correct and a stale cache entry is not.
        return EMPTY_PREFS
      }
      // Server not in yet — the cache is what stops a flash of default columns.
      const cached = cache.current.map.get(tableId)
      if (cached) return cached
      const read = readTablePrefs(scope, tableId)
      cache.current.map.set(tableId, read)
      return read
    },
    [byTable, scope, serverMap],
  )

  const setPrefsFor = useCallback(
    (tableId: string, next: TableColumnPrefs) => {
      writeTablePrefs(scope, tableId, next)
      cache.current.map.set(tableId, next)
      setByTable((prev) => ({ ...prev, [tableId]: next }))
      schedule(tableId, next)
    },
    [scope, schedule],
  )

  const resetTable = useCallback(
    (tableId: string) => {
      clearTablePrefs(scope, tableId)
      cache.current.map.set(tableId, EMPTY_PREFS)
      setByTable((prev) => ({ ...prev, [tableId]: EMPTY_PREFS }))
      schedule(tableId, null)
    },
    [scope, schedule],
  )

  const resetAll = useCallback(() => {
    clearAllTablePrefs(scope)
    cache.current = { scope, map: new Map() }
    setByTable({})
    for (const tableId of [...timers.current.keys()]) {
      clearTimeout(timers.current.get(tableId)!)
      timers.current.delete(tableId)
    }
    pending.current.clear()
    // Prefix-scoped, so this clears table layouts and nothing else a later
    // feature might store under its own namespace.
    void userPreferencesApi
      .removeAll(PREFERENCE_KEY_PREFIX)
      .then(() =>
        // Only the table layouts go: other preferences share this cached map
        // (the Overall Status board's settings) and were not deleted.
        queryClient.setQueryData<Record<string, unknown>>(TABLE_PREFS_QUERY_KEY, prev =>
          Object.fromEntries(Object.entries(prev ?? {}).filter(([key]) => !key.startsWith(PREFERENCE_KEY_PREFIX))),
        ),
      )
      .catch(() => {})
  }, [scope, queryClient])

  // From the server map, so it counts layouts saved in an earlier session or on
  // another machine — not only tables mounted in this tab.
  const customizedTableIds = useMemo(() => {
    const ids = new Set<string>()
    for (const key of Object.keys(serverMap ?? {})) {
      if (key.startsWith(PREFERENCE_KEY_PREFIX)) ids.add(key.slice(PREFERENCE_KEY_PREFIX.length))
    }
    for (const [tableId, prefs] of Object.entries(byTable)) {
      if (hasSavedPrefs(prefs)) ids.add(tableId)
      else ids.delete(tableId)
    }
    return [...ids].sort()
  }, [serverMap, byTable])

  const value = useMemo<TableColumnPrefsContextValue>(
    () => ({ scope, prefsFor, setPrefsFor, resetTable, resetAll, customizedTableIds }),
    [scope, prefsFor, setPrefsFor, resetTable, resetAll, customizedTableIds],
  )

  return (
    <TableColumnPrefsContext.Provider value={value}>{children}</TableColumnPrefsContext.Provider>
  )
}

const FALLBACK_STORE: TableColumnPrefsContextValue = {
  scope: 'anon',
  prefsFor: () => EMPTY_PREFS,
  setPrefsFor: () => {},
  resetTable: () => {},
  resetAll: () => {},
  customizedTableIds: [],
}

/**
 * The raw store. Most callers want `useTableColumns` instead — this is for the
 * app-level controls (a "reset all layouts" button in settings).
 *
 * Falls back to a no-op store with NO PROVIDER rather than throwing. Column
 * preferences are a convenience, and a table rendered outside the provider (a
 * test harness, an isolated preview) should still render at its defaults instead
 * of crashing the screen — the same reasoning as `useFeatureFlag`. Nothing about
 * correctness or access depends on this.
 */
export function useTableColumnPrefsStore(): TableColumnPrefsContextValue {
  const ctx = useContext(TableColumnPrefsContext)
  return ctx ?? FALLBACK_STORE
}

/** Exported for the tests that pin the cache key shape. */
export { tablePrefsKey }
